import React, { useEffect, useMemo, useState } from "react";
import AppHeader from "../../../components/Header/AppHeader";
import DeleteConfirmationModal from "../../../components/DeleteConfirmationModal";
import { apiWrapWithErrorWithData } from "../../../utilities/apiHelpers";
import {
  addParty,
  deleteParty,
  getPartiesList,
  updateParty,
} from "../../../utilities/apis/parties";
import {
  ActionIcon,
  Button,
  Card,
  Grid,
  Group,
  Modal,
  Pagination,
  Select,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconEdit,
  IconPlus,
  IconSearch,
  IconTrash,
  IconUsers,
  IconX,
} from "@tabler/icons-react";
import { showNotification } from "@mantine/notifications";
import { BeatLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import { validateEmail } from "../../../utilities/utilities";
import colors from "../../../utilities/design";

const initialFormState = {
  name: "",
  entityType: "Company",
  brandName: "",
  email: "",
  signatory: "",
  representative: "",
  address: "",
  corporateAddress: "",
  pocphone: "",
};

const mapPartyToForm = (party = {}) => ({
  id: party.id,
  name: party.name || "",
  entityType: party.companyType || "Company",
  brandName: party.brandName || "",
  email: party.email || "",
  signatory: party.signatory || "",
  representative: party.representative || "",
  address: party.registeredOfficeAddress || "",
  corporateAddress: party.corporateOfficeAddress || "",
  pocphone: party.phone || "",
});

const mapFormToPayload = (form) => ({
  ...(form.id ? { id: form.id } : {}),
  type: "Entity",
  name: form.name?.trim(),
  companyType: form.entityType,
  entityType: form.entityType,
  brandName: form.brandName,
  email: form.email?.trim(),
  signatory: form.signatory?.trim(),
  representative: form.representative?.trim(),
  registeredOfficeAddress: form.address,
  corporateOfficeAddress: form.corporateAddress,
  phone: form.pocphone,
});

const PAGE_SIZE_OPTIONS = ["10", "20", "50"];

const PartyList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [parties, setParties] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [editId, setEditId] = useState(0);
  const [formState, setFormState] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState({});
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [partyToDelete, setPartyToDelete] = useState(null);

  const fetchParties = async () => {
    setLoading(true);
    const response = await apiWrapWithErrorWithData(getPartiesList());

    if (response?.success) {
      const partyList = Array.isArray(response.data) ? response.data : [];
      setParties(
        partyList.filter((party) => (party.type || "Entity") === "Entity"),
      );
    } else {
      setParties([]);
      showNotification({
        color: "red",
        title: "Parties",
        message: response?.message || "Could not load parties.",
      });
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchParties();
  }, []);

  const filteredParties = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return parties.filter((party) => {
      if ((party.type || "Entity") !== "Entity") return false;

      return (
        !search ||
        (party.name || "").toLowerCase().includes(search) ||
        (party.email || "").toLowerCase().includes(search) ||
        (party.signatory || "").toLowerCase().includes(search) ||
        (party.companyType || "").toLowerCase().includes(search) ||
        (party.brandName || "").toLowerCase().includes(search)
      );
    });
  }, [parties, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredParties.length / pageSize));

  const paginatedParties = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredParties.slice(start, start + pageSize);
  }, [filteredParties, page, pageSize]);

  const showingFrom = filteredParties.length ? (page - 1) * pageSize + 1 : 0;
  const showingTo = Math.min(page * pageSize, filteredParties.length);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditId(0);
    setFormState(initialFormState);
    setFormErrors({});
  };

  const openCreateModal = () => {
    setEditId(0);
    setFormState(initialFormState);
    setFormErrors({});
    setModalOpen(true);
  };

  const openEditModal = (party) => {
    if ((party.type || "Entity") !== "Entity") return;

    setEditId(party.id || 0);
    setFormState(mapPartyToForm(party));
    setFormErrors({});
    setModalOpen(true);
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!formState.name?.trim()) {
      nextErrors.name = "Registered name is required";
    }
    if (!formState.entityType) {
      nextErrors.entityType = "Entity type is required";
    }
    if (!formState.signatory?.trim()) {
      nextErrors.signatory = "Authorised signatory name is required";
    }
    if (!formState.email?.trim()) {
      nextErrors.email = "Authorised signatory email is required";
    } else if (!validateEmail(formState.email)) {
      nextErrors.email = "Enter a valid email";
    }

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSaveParty = async () => {
    if (!validateForm()) return;

    setFormLoading(true);
    const payload = mapFormToPayload(formState);
    const response = await apiWrapWithErrorWithData(
      editId > 0
        ? updateParty({ partyData: payload })
        : addParty({ partyData: payload }),
    );

    if (response?.success) {
      showNotification({
        color: "green",
        title: editId > 0 ? "Party updated" : "Party added",
        message:
          response?.message ||
          (editId > 0
            ? "Party details were updated successfully."
            : "Party was added successfully."),
      });
      handleCloseModal();
      fetchParties();
    } else {
      showNotification({
        color: "red",
        title: editId > 0 ? "Update party" : "Add party",
        message:
          response?.message ||
          (editId > 0
            ? "Could not update the party."
            : "Could not add the party."),
      });
    }

    setFormLoading(false);
  };

  const handleDeleteParty = async () => {
    if (!partyToDelete?.id) return;

    setDeleteLoading(true);
    const response = await apiWrapWithErrorWithData(
      deleteParty({ id: partyToDelete.id }),
    );

    if (response?.success) {
      showNotification({
        color: "green",
        title: "Party deleted",
        message: response?.message || "Party was deleted successfully.",
      });
      setDeleteModalOpen(false);
      setPartyToDelete(null);
      fetchParties();
    } else {
      showNotification({
        color: "red",
        title: "Delete party",
        message: response?.message || "Could not delete the party.",
      });
    }

    setDeleteLoading(false);
  };

  const updateField = (field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:p-4">
        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <Title order={2}>Entity Party Management</Title>
            <Text size="sm" color="dimmed" mt={4}>
              View, add, edit, and delete entity parties used across agreements.
            </Text>
          </div>
          <Button
            className="rounded-lg bg-[#46BDE1] text-white hover:bg-[#3aa8ca]"
            leftIcon={<IconPlus size={16} />}
            onClick={openCreateModal}
          >
            Add Entity Party
          </Button>
        </div>

        <Card
          shadow="sm"
          radius="md"
          p="md"
          withBorder
          className="mb-6 bg-white"
        >
          <TextInput
            label="Search entity parties"
            placeholder="Search by name, email, signatory, entity type, or brand..."
            value={searchTerm}
            icon={<IconSearch size={16} className="text-gray-400" />}
            rightSection={
              searchTerm ? (
                <ActionIcon
                  onClick={() => setSearchTerm("")}
                  size="sm"
                  radius="xl"
                >
                  <IconX size={14} />
                </ActionIcon>
              ) : null
            }
            onChange={(event) => setSearchTerm(event.currentTarget.value)}
          />
        </Card>

        <Card
          shadow="sm"
          radius="md"
          p={0}
          withBorder
          className="overflow-hidden bg-white"
        >
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-4 py-20">
              <BeatLoader color={colors.primary} size={12} />
              <Text size="sm" color="dimmed">
                Loading parties...
              </Text>
            </div>
          ) : filteredParties.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-4 py-16 text-center">
              <div className="mb-3 rounded-full bg-gray-50 p-4 text-gray-400">
                <IconUsers size={32} />
              </div>
              <Text size="md" className="font-semibold text-gray-900">
                No entity parties found
              </Text>
              <Text size="sm" color="dimmed" className="mt-1 max-w-sm">
                {searchTerm
                  ? "Try adjusting your search."
                  : "Add your first entity party to get started."}
              </Text>
              {!searchTerm && (
                <Button
                  size="sm"
                  className="mt-4 rounded-lg bg-[#46BDE1] text-white hover:bg-[#3aa8ca]"
                  leftIcon={<IconPlus size={14} />}
                  onClick={openCreateModal}
                >
                  Add Entity Party
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table striped highlightOnHover>
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                        Name
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                        Entity Type
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                        Brand
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                        Signatory
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                        Email
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                        Phone
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedParties.map((party) => (
                      <tr key={party.id} className="border-t border-gray-100">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {party.name || "-"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {party.companyType || "-"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {party.brandName || "-"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {party.signatory || "-"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {party.email || "-"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {party.phone || "-"}
                        </td>
                        <td className="px-4 py-3">
                          <Group spacing="xs" position="right">
                            <Tooltip label="Edit party">
                              <ActionIcon
                                color="blue"
                                variant="light"
                                onClick={() => openEditModal(party)}
                              >
                                <IconEdit size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Delete party">
                              <ActionIcon
                                color="red"
                                variant="light"
                                onClick={() => {
                                  setPartyToDelete(party);
                                  setDeleteModalOpen(true);
                                }}
                              >
                                <IconTrash size={18} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <div className="flex flex-col gap-3 border-t border-gray-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                <Text size="sm" color="dimmed">
                  Showing {showingFrom}-{showingTo} of {filteredParties.length}{" "}
                  parties
                </Text>
                <div className="flex flex-col items-center gap-3 sm:flex-row">
                  <Pagination
                    page={page}
                    onChange={setPage}
                    total={totalPages}
                  />
                  <div className="flex items-center gap-2">
                    <Text size="sm" color="dimmed">
                      Page size
                    </Text>
                    <Select
                      className="w-20"
                      data={PAGE_SIZE_OPTIONS}
                      value={String(pageSize)}
                      onChange={(value) => {
                        setPageSize(Number(value));
                        setPage(1);
                      }}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </Card>
      </div>

      <Modal
        opened={modalOpen}
        onClose={handleCloseModal}
        title={editId > 0 ? "Edit entity party" : "Add entity party"}
        size="xl"
        centered
      >
        <div className="flex flex-col gap-4">
          <Grid>
            <Grid.Col span={12} md={6}>
              <TextInput
                required
                label="Registered Name"
                value={formState.name}
                error={formErrors.name}
                onChange={(event) =>
                  updateField("name", event.currentTarget.value)
                }
              />
            </Grid.Col>
            <Grid.Col span={12} md={6}>
              <Select
                required
                label="Entity Type"
                value={formState.entityType}
                error={formErrors.entityType}
                onChange={(value) => updateField("entityType", value)}
                data={[
                  { value: "Company", label: "Company" },
                  { value: "LLP", label: "LLP" },
                  { value: "Partnership Firm", label: "Partnership Firm" },
                  { value: "Sole Proprietary", label: "Sole Proprietary" },
                ]}
              />
            </Grid.Col>
            <Grid.Col span={12} md={6}>
              <TextInput
                label="Brand Name"
                value={formState.brandName}
                onChange={(event) =>
                  updateField("brandName", event.currentTarget.value)
                }
              />
            </Grid.Col>
            <Grid.Col span={12} md={6}>
              <TextInput
                required
                label="Authorised Signatory Name"
                value={formState.signatory}
                error={formErrors.signatory}
                onChange={(event) =>
                  updateField("signatory", event.currentTarget.value)
                }
              />
            </Grid.Col>
            <Grid.Col span={12} md={6}>
              <TextInput
                required
                label="Authorised Signatory Email"
                value={formState.email}
                error={formErrors.email}
                onChange={(event) =>
                  updateField("email", event.currentTarget.value)
                }
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <Textarea
                label="Registered Address"
                value={formState.address}
                onChange={(event) =>
                  updateField("address", event.currentTarget.value)
                }
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <Textarea
                label="Corporate Address"
                value={formState.corporateAddress}
                onChange={(event) =>
                  updateField("corporateAddress", event.currentTarget.value)
                }
              />
            </Grid.Col>
            <Grid.Col span={12} md={6}>
              <TextInput
                label="Business POC"
                value={formState.representative}
                onChange={(event) =>
                  updateField("representative", event.currentTarget.value)
                }
              />
            </Grid.Col>
            <Grid.Col span={12} md={6}>
              <TextInput
                label="Business POC Phone"
                value={formState.pocphone}
                maxLength={10}
                onChange={(event) =>
                  updateField(
                    "pocphone",
                    event.currentTarget.value.replace(/\D/g, ""),
                  )
                }
              />
            </Grid.Col>
          </Grid>

          <Group position="right" mt="md">
            <Button variant="default" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button
              className="rounded-lg bg-[#46BDE1] text-white hover:bg-[#3aa8ca]"
              loading={formLoading}
              onClick={handleSaveParty}
            >
              {editId > 0 ? "Save Changes" : "Add Entity Party"}
            </Button>
          </Group>
        </div>
      </Modal>

      <DeleteConfirmationModal
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        deleteLoading={deleteLoading}
        deleteHandler={handleDeleteParty}
        title="Delete Party"
        text={`Are you sure you want to delete "${partyToDelete?.name || "this party"}"? This action cannot be undone.`}
      />
    </>
  );
};

export default PartyList;
